"use client";

import { isNextClient } from "@perk/core/lib/is-next-client";
import * as React from "react";
import { NextImage } from "./image-shim";

const BASE_URL = process.env.NEXT_PUBLIC_FRONTEND_BASE_URL;

export interface UniversalImageProps
  extends Omit<
    React.ImgHTMLAttributes<HTMLImageElement>,
    "src" | "alt" | "width" | "height"
  > {
  src: string;
  alt: string;
  width?: number | string;
  height?: number | string;
  fallback?: string;
  fill?: boolean;
  sizes?: string;
  quality?: number;
  priority?: boolean;
  placeholder?: "blur" | "empty" | string;
  blurDataURL?: string;
  unoptimized?: boolean;
  objectFit?: React.CSSProperties["objectFit"];
  loader?: (props: { src: string; width: number; quality?: number }) => string;
  overrideSrc?: string;
  onLoadingComplete?: (img: HTMLImageElement) => void;
}

/**
 * The Universal Image component:
 * - Uses Next.js' <Image> if available (and if running in a Next environment)
 * - Falls back to a plain <img> (PlainImg) otherwise.
 */
export const Image = React.forwardRef<HTMLImageElement, UniversalImageProps>(
  function UniversalImageImpl(
    {
      src,
      alt,
      width,
      height,
      fill,
      sizes: _sizes,
      quality: _quality,
      priority: _priority,
      placeholder: _placeholder,
      blurDataURL: _blurDataURL,
      unoptimized: _unoptimized,
      fallback,
      objectFit,
      overrideSrc,
      onLoad,
      onError,
      onLoadingComplete: _onLoadingComplete,
      loader: _loader,
      style,
      ...rest
    },
    ref
  ) {
    if (!src && fallback) {
      src = fallback;
    }

    // prepend BASE_URL to src and fallback
    const fullSrc = src?.startsWith("/") ? `${BASE_URL}${src}` : src;
    const fullFallback = fallback?.startsWith("/")
      ? `${BASE_URL}${fallback}`
      : fallback;

    // Common error handler for both implementations.
    const handleError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
      if (!onError && fullFallback) {
        const currentSrc = e.currentTarget.src;
        if (!currentSrc.endsWith(fullFallback)) {
          e.currentTarget.src = fullFallback;
        } else {
          e.currentTarget.onerror = null;
        }
      }
      onError?.(e);
    };

    // If NextImage is available and we're in a Next.js environment, render it.
    if (NextImage && isNextClient()) {
      return (
        // @ts-expect-error: NextImage is not typed.
        <NextImage
          ref={ref}
          src={fullSrc}
          alt={alt}
          width={width}
          height={height}
          fill={fill}
          sizes={_sizes}
          quality={_quality}
          priority={_priority}
          placeholder={_placeholder}
          blurDataURL={_blurDataURL}
          unoptimized={_unoptimized}
          loader={_loader}
          onLoad={onLoad}
          onError={handleError}
          onLoadingComplete={_onLoadingComplete}
          style={{
            objectFit,
            ...style,
          }}
          {...rest}
        />
      );
    }

    // Otherwise, fall back to PlainImg.
    return (
      <PlainImg
        ref={ref}
        src={fullSrc}
        alt={alt}
        width={width}
        height={height}
        fill={fill}
        objectFit={objectFit}
        overrideSrc={overrideSrc}
        style={style}
        onLoad={onLoad}
        onError={handleError}
        priority={_priority}
        onLoadingComplete={_onLoadingComplete}
        {...rest}
      />
    );
  }
);

/**
 * The fallback plain <img> implementation.
 * (You may further adjust this to remove the transparent GIF placeholder for priority images.)
 */
const PlainImg = React.forwardRef<HTMLImageElement, UniversalImageProps>(
  function PlainImgImpl(
    {
      src,
      alt,
      width,
      height,
      fill,
      objectFit,
      overrideSrc,
      style,
      onLoad,
      onError,
      // Explicitly omit Next.js specific props
      sizes: _sizes,
      quality: _quality,
      priority: _priority,
      placeholder: _placeholder,
      blurDataURL: _blurDataURL,
      unoptimized: _unoptimized,
      loader: _loader,
      onLoadingComplete: _onLoadingComplete,
      ...props
    },
    ref
  ) {
    const numericWidth =
      typeof width === "string" ? Number.parseInt(width, 10) : width;
    const numericHeight =
      typeof height === "string" ? Number.parseInt(height, 10) : height;

    const finalSrc = overrideSrc || src || undefined; // Use undefined for empty string to avoid warning

    const imgStyle: React.CSSProperties = {
      ...(fill
        ? {
            position: "absolute",
            height: "100%",
            width: "100%",
            left: 0,
            top: 0,
            right: 0,
            bottom: 0,
          }
        : {}),
      objectFit,
      ...style,
    };

    return (
      // biome-ignore lint/a11y/useAltText: <explanation>
      <img
        ref={ref}
        src={finalSrc}
        alt={alt}
        width={!fill ? numericWidth : undefined}
        height={!fill ? numericHeight : undefined}
        style={imgStyle}
        onLoad={(e) => {
          onLoad?.(e);
          // Handle onLoadingComplete as a normal onLoad in non-Next environments
          if (_onLoadingComplete) {
            _onLoadingComplete(e.currentTarget);
          }
        }}
        onError={onError}
        {...props}
      />
    );
  }
);

PlainImg.displayName = "PlainImg";
Image.displayName = "Image";

export default Image;
